import { BffApiClientMolecule } from "@/app/api-client/store"
import type { Atomize } from "@/app/reatom/reatom-type"
import { ThirdwebMoleculeReatom } from "@/app/vendors/thirdweb/thirdweb-molecule.reatom"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import {
  type DropPhase,
  DropPhaseType,
  DropStatus,
  DropType,
  type DropUserClaimLimitInfo,
  FeeType,
  type MinimalDrop,
} from "@anaconda/core/drop/types"
import { UNKNOWN_CURRENCY } from "@anaconda/core/price/types"
import { msg } from "@lingui/core/macro"
import { action, atom, computed, withAsyncData, wrap } from "@reatom/core"
import { molecule } from "bunshi"
import { getEligibilityQuery } from "./get-eligibility-query"
import { DropIdScope, DropScope } from "./scope"

/**
 * Simplified molecule for grid view - has eligibility logic but NO auto-refetch effects.
 * Uses batch minted data from parent instead of individual fetches.
 */
export const DropGridMoleculeReatom = molecule((mol, scope) => {
  const { unionAddress, initialized } = mol(WalletConnectMoleculeReatom)
  const { reatomContract } = mol(ThirdwebMoleculeReatom)

  const { dropId } = scope(DropIdScope)
  const dropAtom = scope(DropScope)

  // Contract atom
  const contractAtom = computed(() => {
    const atomizedDrop = dropAtom()
    if (!atomizedDrop) return undefined
    try {
      const id = atomizedDrop.id()
      const blockchain = atomizedDrop.blockchain()
      return reatomContract(id, blockchain)()
    } catch (error) {
      return undefined
    }
  }, `contractAtom-${dropId}`)

  // Core atoms
  const nowAtom = atom(() => Date.now(), `nowAtom-${dropId}`)

  const phasesAtom = computed(() => {
    const atomizedDrop = dropAtom()
    if (!atomizedDrop) return []
    return atomizedDrop.phases()
  }, `phasesAtom-${dropId}`)

  const hasMultiplePhasesAtom = computed(
    () => phasesAtom().length > 1,
    "hasMultiplePhasesAtom",
  )

  const getCurrentPhase = (phases: DropPhase[], now: number) => {
    return phases.find((phase: DropPhase) => {
      const startDate = new Date(phase.startDate).getTime()
      const endDate = phase.endDate
        ? new Date(phase.endDate).getTime()
        : undefined

      return startDate <= now && (!endDate || now < endDate)
    })
  }

  const currentPhaseAtom = computed(() => {
    const phasesArray = phasesAtom()
    const now = nowAtom()

    if (phasesArray.length === 0) {
      throw new Error("No current phase found")
    }

    const activePhase = getCurrentPhase(phasesArray, now)
    if (activePhase) return activePhase

    return phasesArray[0]
  }, "currentPhaseAtom")

  // Fetch user eligibility - one-time fetch, no auto-refetch
  const userEligibilityDataAtom = computed(async () => {
    const atomizedDrop = dropAtom()
    if (!atomizedDrop) return null

    const address = unionAddress()
    const currentPhase = currentPhaseAtom()
    const contract = contractAtom()

    const eligibilityQuery = getEligibilityQuery({
      dropId: atomizedDrop.id(),
      dropType: atomizedDrop.type(),
      address,
      currentPhaseType: currentPhase?.type,
      contract,
    })

    const result = await wrap(eligibilityQuery.queryFn())
    return result
  }, `userEligibilityDataAtom-${dropId}`).extend(
    withAsyncData({
      initState: { isEligible: false, limitExceeded: false } as {
        isEligible: boolean
        limitExceeded: boolean
        claimLimitInfo?: DropUserClaimLimitInfo
      },
    }),
  )

  const isThirdwebDropAtom = computed(() => {
    const atomizedDrop = dropAtom()
    return atomizedDrop?.type() === DropType.Thirdweb
  }, "isThirdwebDropAtom")

  const isUserEligibleAtom = computed(() => {
    const data = userEligibilityDataAtom.data()
    return data?.isEligible ?? false
  }, "isUserEligibleAtom")

  const userLimitExceededAtom = computed(() => {
    const data = userEligibilityDataAtom.data()
    return data?.limitExceeded ?? false
  }, "userLimitExceededAtom")

  const userClaimLimitInfoAtom = computed(() => {
    const data = userEligibilityDataAtom.data()
    return data?.claimLimitInfo
  }, "userClaimLimitInfoAtom")

  const userClaimLimitForMintAtom = computed(() => {
    const claimLimitInfo = userClaimLimitInfoAtom()
    if (!claimLimitInfo) return undefined

    return {
      remainingLimit: claimLimitInfo.remainingLimit,
    }
  }, "userClaimLimitForMintAtom")

  const isCurrentPhaseExpiredAtom = computed(() => {
    const currentPhase = currentPhaseAtom()
    if (!currentPhase?.endDate) {
      return false
    }
    if (nowAtom() < new Date(currentPhase.endDate).getTime()) {
      return false
    }

    return true
  }, "isCurrentPhaseExpiredAtom")

  const isCurrentPhaseUpcomingAtom = computed(() => {
    const currentPhase = currentPhaseAtom()
    if (!currentPhase?.startDate) {
      return false
    }
    if (nowAtom() > new Date(currentPhase.startDate).getTime()) {
      return false
    }

    return true
  }, "isCurrentPhaseUpcomingAtom")

  const isMintDisabledForUserAtom = computed(() => {
    const isUserEligible = isUserEligibleAtom()
    const userLimitExceeded = userLimitExceededAtom()

    return userLimitExceeded || !isUserEligible
  }, "isMintDisabledForUserAtom")

  const isMintDisabledAtom = computed(() => {
    const isCurrentPhaseExpired = isCurrentPhaseExpiredAtom()
    const isCurrentPhaseUpcoming = isCurrentPhaseUpcomingAtom()
    const isWalletConnectorInitialized = initialized()

    return (
      isCurrentPhaseExpired ||
      isCurrentPhaseUpcoming ||
      !isWalletConnectorInitialized
    )
  }, "isMintDisabledAtom")

  const mintNotAvailableMessageAtom = computed(() => {
    const isExpired = isCurrentPhaseExpiredAtom()
    const isUpcoming = isCurrentPhaseUpcomingAtom()
    const isUserEligible = isUserEligibleAtom()
    const userLimitExceeded = userLimitExceededAtom()

    if (isExpired) {
      return msg`The drop has ended`
    }

    if (isUpcoming) {
      return msg`The drop has not started yet`
    }

    if (userLimitExceeded) {
      return msg`You have reached the maximum mint limit`
    }

    if (!isUserEligible) {
      return msg`You're not eligible to mint in this phase`
    }

    return null
  }, "mintNotAvailableMessageAtom")

  const dropDisplayPriceAtom = computed(() => {
    const atomizedDrop = dropAtom()
    const currentPhase = currentPhaseAtom()

    if (!atomizedDrop || !currentPhase) {
      return { amount: "0", currency: UNKNOWN_CURRENCY }
    }

    const price = currentPhase.price
    if (!price) {
      return { amount: "0", currency: UNKNOWN_CURRENCY }
    }

    return {
      amount: price.amount,
      currency: price.currency,
    }
  }, `dropDisplayPriceAtom-${dropId}`)

  const dropFeeAtom = computed(() => {
    const atomizedDrop = dropAtom()
    const currentPhase = currentPhaseAtom()
    const dropFees = atomizedDrop?.fees()

    if (!currentPhase) {
      return { amount: "0", currency: UNKNOWN_CURRENCY }
    }

    if (!dropFees || dropFees.length === 0) {
      return { amount: "0", currency: UNKNOWN_CURRENCY }
    }

    const feesAmount = dropFees.reduce((acc: number, fee) => {
      const feeValue =
        fee.type === FeeType.FLAT
          ? fee.amount
          : fee.amount * Number(currentPhase.price.amount)

      return acc + feeValue
    }, 0)

    return {
      ...currentPhase.price,
      amount: feesAmount.toString(),
    }
  }, `dropFeeAtom-${dropId}`)

  return {
    dropAtom,
    contractAtom,
    currentPhaseAtom,
    isThirdwebDropAtom,
    phasesAtom,
    hasMultiplePhasesAtom,
    userEligibilityDataAtom,
    isUserEligibleAtom,
    userLimitExceededAtom,
    userClaimLimitInfoAtom,
    userClaimLimitForMintAtom,
    isMintDisabledForUserAtom,
    isCurrentPhaseExpiredAtom,
    isCurrentPhaseUpcomingAtom,
    isMintDisabledAtom,
    mintNotAvailableMessageAtom,
    dropDisplayPriceAtom,
    dropFeeAtom,
    nowAtom,
  }
})
